// =============================================================================
// FILE: packages/renderer/src/components/purchase/PurchaseRequestModal.tsx
// PURPOSE: The manual purchase flow. A customer selects an R2 package, submits
//   their details, and receives the E-transfer notice + a reference number.
//   Creates a purchase_request; the Founder releases the license from the inbox.
// =============================================================================

import { useState, type JSX } from 'react';
import { X, ShoppingCart, CheckCircle2, Loader2 } from 'lucide-react';
import { ManualPaymentNotice } from './ManualPaymentNotice.js';
import { submitPurchaseRequest } from '../../lib/purchaseRequests.js';

export interface PurchaseTarget { id: string; name: string; category?: string; price?: string; }

export function PurchaseRequestModal({ product, onClose }: { product: PurchaseTarget; onClose: () => void }): JSX.Element {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [company, setCompany] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<{ id: string } | null>(null);

  const submit = async () => {
    setError(null);
    if (!name.trim() || !email.includes('@')) { setError('Please enter your name and a valid email.'); return; }
    setBusy(true);
    try {
      const res = await submitPurchaseRequest({
        customer_name: name.trim(), email: email.trim(), company: company.trim() || undefined,
        product_id: product.id, product_name: product.name, product_category: product.category, price: product.price,
      });
      setDone({ id: res.id });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not submit request. Please try again.');
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="purchase-request-modal" className="fixed inset-0 z-[10001] grid place-items-center p-6"
      style={{ background: 'rgba(4,7,14,0.9)', backdropFilter: 'blur(8px)' }}>
      <div className="relative w-full max-w-lg rounded-2xl overflow-hidden"
        style={{ background: 'rgba(9,13,22,0.98)', border: '1px solid rgba(245,158,11,0.35)', boxShadow: '0 30px 120px rgba(0,0,0,0.75)' }}>
        <div className="flex items-center justify-between px-6 py-4" style={{ borderBottom: '1px solid rgba(245,158,11,0.2)' }}>
          <div className="flex items-center gap-3">
            <ShoppingCart className="h-5 w-5 text-amber-300" />
            <div>
              <div className="text-[10px] uppercase tracking-[0.24em] text-amber-300/80">R2 Instant Design · Purchase</div>
              <div className="text-sm font-bold text-zinc-100">{product.name}{product.price ? ` · ${product.price}` : ''}</div>
            </div>
          </div>
          <button data-testid="purchase-modal-close" onClick={onClose} className="h-8 w-8 grid place-items-center rounded-md text-zinc-500 hover:text-zinc-200" style={{ border: '1px solid rgba(63,63,70,0.6)' }}><X className="h-4 w-4" /></button>
        </div>

        <div className="p-6 space-y-4">
          <ManualPaymentNotice />

          {done ? (
            <div data-testid="purchase-success" className="rounded-xl p-4 text-center" style={{ background: 'rgba(52,211,153,0.08)', border: '1px solid rgba(52,211,153,0.3)' }}>
              <CheckCircle2 className="h-8 w-8 text-emerald-400 mx-auto mb-2" />
              <div className="text-[14px] font-bold text-zinc-100">Request received</div>
              <p className="text-[12px] text-zinc-400 mt-1">Send your E-transfer, then your license will be released after verification.</p>
              <div className="text-[11px] text-zinc-500 mt-2">Reference: <span className="font-mono text-amber-300">{done.id.slice(0, 8).toUpperCase()}</span></div>
              <button data-testid="purchase-done-close" onClick={onClose} className="mt-4 px-5 py-2 rounded-lg text-[12px] font-bold bg-amber-300 text-zinc-900 hover:bg-amber-200">Done</button>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 gap-3">
                <label className="text-[10px] uppercase tracking-wide text-zinc-500">Full Name
                  <input data-testid="purchase-name" value={name} onChange={(e) => setName(e.target.value)}
                    className="mt-1 w-full px-3 py-2 rounded-lg text-[13px] text-zinc-100 outline-none" style={{ background: 'rgba(2,6,12,0.6)', border: '1px solid rgba(148,163,184,0.25)' }} />
                </label>
                <label className="text-[10px] uppercase tracking-wide text-zinc-500">Email
                  <input data-testid="purchase-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                    className="mt-1 w-full px-3 py-2 rounded-lg text-[13px] text-zinc-100 outline-none" style={{ background: 'rgba(2,6,12,0.6)', border: '1px solid rgba(148,163,184,0.25)' }} />
                </label>
                <label className="text-[10px] uppercase tracking-wide text-zinc-500">Company <span className="text-zinc-600">(optional)</span>
                  <input data-testid="purchase-company" value={company} onChange={(e) => setCompany(e.target.value)}
                    className="mt-1 w-full px-3 py-2 rounded-lg text-[13px] text-zinc-100 outline-none" style={{ background: 'rgba(2,6,12,0.6)', border: '1px solid rgba(148,163,184,0.25)' }} />
                </label>
              </div>
              {error && <div data-testid="purchase-error" className="text-[12px] text-red-400">{error}</div>}
              <button data-testid="purchase-submit" onClick={submit} disabled={busy}
                className="w-full flex items-center justify-center gap-2 rounded-lg px-5 py-3 font-bold text-[13px] uppercase tracking-[0.12em] transition-transform hover:scale-[1.01] disabled:opacity-60"
                style={{ background: '#f59e0b', color: '#1a1204' }}>
                {busy ? <><Loader2 className="h-4 w-4 animate-spin" /> Submitting…</> : 'Submit Purchase Request'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
